import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';
import { getAuthTransport } from '@/lib/api/authTransport';

/**
 * 증명서 발급 (docs/18_2026-07-31_multi-ai-agent-design.md §10).
 *
 * 서식 목록은 평범한 JSON 조회지만 **미리보기는 PDF 바이트**라 `apiFetch` 를 쓰지 않는다 —
 * 그 함수는 `ApiResponse` 봉투를 벗겨 JSON 을 돌려주는 것이 일이라, 여기서 쓰면 PDF 를 문자열로
 * 망가뜨린다. 대신 인증 헤더·credentials 는 같은 `AuthTransport` 에서 가져와 쿠키·CSRF 규약을 맞춘다.
 */

export type IssuanceTemplate = {
  templateKey: string;
  name: string;
  description: string | null;
  requiresApproval: boolean;
  /** SELF = 본인 것만 · HR = 인사 권한자가 타인 것도 */
  subjectPolicy: 'SELF' | 'HR';
  /** 결재를 타면 올라갈 결재 서식. 미리보기가 이 서식으로 결재선을 물어 기안서를 함께 그린다. 옛 서버엔 없다. */
  approvalFormKey?: string | null;
};

export type PreviewInput = {
  templateKey: string;
  purpose?: string;
  recipient?: string;
  /** 비우면 본인. 남의 id 를 넣어도 권한이 없으면 서버가 거절한다. */
  subjectPersonnelId?: string;
};

/** 발급 이력 한 줄. 서버가 개인정보(스냅샷)와 저장소 키는 빼고 내려준다. */
export type Issuance = {
  id: string;
  templateKey: string;
  templateName: string;
  /** DRAFT = 반려되어 되돌아옴 · PENDING_APPROVAL = 결재 대기 · ISSUED = 발급 완료 · REVOKED = 회수 */
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'ISSUED' | 'REVOKED';
  docNo: string | null;
  purpose: string | null;
  recipient: string | null;
  copies: number;
  /** 직인이 찍혔는가. 직인 미등록 상태로 발급된 문서를 화면이 구분해 보인다. */
  sealed: boolean;
  approvalDocumentId: string | null;
  issuedAt: string | null;
  createdAt: string;
  /** 담당자 — 그 결재의 전결자. 결재 없이 나간 발급과 옛 서버에서는 오지 않는다(null 칸은 빠진다). */
  finalApproverName?: string | null;
  finalApproverPersonnelId?: string | null;
};

export type IssuanceInput = PreviewInput & { copies: number };

export const issuanceTemplatesQueryKey = ['issuance-templates'] as const;
export const myIssuancesQueryKey = ['my-issuances'] as const;

/**
 * 발급 서식 목록.
 *
 * `enabled` 를 받는 이유: AI 대화가 이 목록을 쓰는데(초안 카드의 서식 이름), 그 대화는 이제
 * 앱 전역에 provider 로 얹혀 있다. 늘 켜 두면 **AI 를 열지도 않은 사람의 모든 화면**에서
 * 이 조회가 한 번씩 나간다.
 */
export function useIssuanceTemplates(enabled = true) {
  return useQuery({
    enabled,
    queryKey: issuanceTemplatesQueryKey,
    // 경로는 /api 를 빼고 적는다 — runtimeConfig().apiBaseUrl 이 '/api' 를 앞에 붙인다.
    // 여기에 /api 를 또 쓰면 /api/api/v1/... 이 되어 정적 리소스 조회로 떨어진다.
    queryFn: () => apiFetch<IssuanceTemplate[]>('/v1/issuances/templates'),
    // 서식은 인사팀이 가끔 고치는 값이다. 매번 다시 받을 이유가 없다.
    staleTime: 5 * 60 * 1000,
  });
}

/** 내가 요청한 발급 이력. */
export function useMyIssuances() {
  return useQuery({
    queryKey: myIssuancesQueryKey,
    queryFn: () => apiFetch<Issuance[]>('/v1/issuances'),
  });
}

/**
 * 발급 신청.
 *
 * 결재가 필요한 서식이면 `PENDING_APPROVAL` 로, 필요 없는 서식이면 이미 `ISSUED` 로 돌아온다 —
 * 화면은 응답 상태만 보고 분기하면 되고, 어느 서식이 결재를 타는지 알 필요가 없다.
 */
export function useRequestIssuance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: IssuanceInput) =>
      apiFetch<Issuance>('/v1/issuances', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: myIssuancesQueryKey }),
  });
}

/** 발급된 문서 내려받기 주소. 인증은 쿠키로 붙으므로 링크를 그대로 열면 된다. */
export function issuanceFileUrl(id: string): string {
  return `${runtimeConfig().apiBaseUrl}/v1/issuances/${id}/file`;
}

/**
 * 미리보기 PDF 를 받아 브라우저가 열 수 있는 blob URL 로 돌려준다.
 *
 * 호출부는 다 쓴 뒤 반드시 {@link URL.revokeObjectURL} 로 풀어야 한다 — 안 풀면 탭이 살아 있는 동안
 * PDF 가 통째로 메모리에 남는다.
 */
export async function fetchPreviewPdf(input: PreviewInput): Promise<string> {
  const transport = getAuthTransport();
  const baseUrl = runtimeConfig().apiBaseUrl;

  // 쓰기 메서드(POST)라 CSRF 토큰 준비가 먼저다. apiFetch 가 하던 일을 여기서 직접 한다.
  if (transport.ensureReady) {
    await transport.ensureReady(baseUrl);
  }

  const response = await fetch(`${baseUrl}/v1/issuances/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // 오류일 때는 서버가 ApiResponse(JSON) 를 준다. PDF 만 받겠다고 하면 그 응답을 협상하지 못해
      // 정작 "권한이 없습니다" 같은 메시지를 읽을 수 없다.
      Accept: 'application/pdf, application/json',
      ...transport.authHeaders({ unsafe: true }),
    },
    credentials: transport.credentials,
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    // 실패 응답은 PDF 가 아니라 ApiResponse 봉투다. 서버가 준 메시지를 그대로 보여 준다 —
    // "권한이 없습니다" 같은 문구가 사용자에게 가장 정확한 안내다.
    let message = `미리보기를 만들지 못했습니다. (HTTP ${response.status})`;
    try {
      const body = await response.json();
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* 봉투가 아니면 기본 문구를 쓴다 */
    }
    throw new Error(message);
  }

  return URL.createObjectURL(await response.blob());
}
